import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { translations } from "./translations";

const LanguageContext = createContext(null);

const VALID = ["en", "es", "am"];
const STORAGE_KEY = "auna_lang";
// Universal product default. Browser/device language MUST NOT participate here.
const DEFAULT_LANG = "en";

function getPath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), obj);
}

export function LanguageProvider({ children }) {
  const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const initialHasPref = VALID.includes(stored);
  const [lang, setLang] = useState(initialHasPref ? stored : DEFAULT_LANG);
  // Whether an explicit CQH language choice has ever been stored on this device.
  const [hasPreference, setHasPreference] = useState(initialHasPref);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const changeLang = useCallback((l) => {
    const next = VALID.includes(l) ? l : DEFAULT_LANG;
    setLang(next);
    setHasPreference(true);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const t = useCallback(
    (path) => {
      const val = getPath(translations[lang], path);
      if (val != null) return val;
      // am → en → es : Amharic falls back to English, Spanish only as final emergency.
      if (lang === "am") {
        const en = getPath(translations.en, path);
        if (en != null) return en;
      }
      const fallback = getPath(translations.es, path);
      return fallback != null ? fallback : path;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, changeLang, hasPreference, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
